import * as React from 'react';
import { observer } from 'mobx-react';
import { BudgetConfigurationViewStore } from 'nomst/Stores/Budget/budgetConfigurationView.store';
import { FormGroup, Input, Options, Option, Alert } from '@payscale/design';
import { PercentageCalculationRuleOptions, AmountCode, StockDisplayType } from 'typings/enums';
import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';
// import BudgetModalBonusRule from './budgetModalBonusRule';

export interface IBudgetSettingsModalProps {
    budgetConfigurationViewStore: BudgetConfigurationViewStore;
}

interface IBudgetSettingsModalState {
    currentSelection: number;
}

interface IBudgetSelection {
    [name: string]: number;
}

const budgetSelection: IBudgetSelection = {
    // tslint:disable-next-line
    'Single Component Bonus': 0,
    'Multi-component Bonus': 1
};

// const multiBudgetChoices =  ['Single Component Bonus', 'Multi-component Bonus'];

@observer
export default class BudgetSettingsModal extends React.Component<IBudgetSettingsModalProps, IBudgetSettingsModalState> {
    constructor(props: IBudgetSettingsModalProps) {
        super(props);
        this.openDeleteBudgetConfirmation = this.openDeleteBudgetConfirmation.bind(this);
        this.handleChangeManagerOverBudgetCheckbox = this.handleChangeManagerOverBudgetCheckbox.bind(this);
        this.handleChangeRequireCommentOnOverBudgetCheckbox
            = this.handleChangeRequireCommentOnOverBudgetCheckbox.bind(this);
        this.handleChangeStockValueInput = this.handleChangeStockValueInput.bind(this);
        this.handleBlurStockValueInput = this.handleBlurStockValueInput.bind(this);
        this.handleStockDisplayChange = this.handleStockDisplayChange.bind(this);
        this.handleRangeEnforcementCheckboxChanged = this.handleRangeEnforcementCheckboxChanged.bind(this);
        this.handleAssociatedFieldsChanged = this.handleAssociatedFieldsChanged.bind(this);
        this.handleStockAssociatedFieldClick = this.handleStockAssociatedFieldClick.bind(this);
        this.handleClickCloseBudgetSettings = this.handleClickCloseBudgetSettings.bind(this);
        this.handleChangeBudgetName = this.handleChangeBudgetName.bind(this);
        this.handleBonusMultiChoiceChange = this.handleBonusMultiChoiceChange.bind(this);
        this.handleBonusPercentageRuleChange = this.handleBonusPercentageRuleChange.bind(this);
        this.handleVariablePayPercentageRuleChange = this.handleVariablePayPercentageRuleChange.bind(this);
        this.handleAutoCalculatePercentageChange = this.handleAutoCalculatePercentageChange.bind(this);
        this.handleAllowOverrideLumpSumChange = this.handleAllowOverrideLumpSumChange.bind(this);
        this.handleSaveBudget = this.handleSaveBudget.bind(this);
        this.setNumberBonusRules = this.setNumberBonusRules.bind(this);

        const { budgetConfigurationViewStore } = this.props;
        this.state = Object.assign({
            currentSelection: budgetConfigurationViewStore.bonusFieldRules.length > 0 ? 1 : 0
        });
    }

    render(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        if (!budgetConfigurationViewStore.showBudgetSettingsModal) {
            return null;
        }

        const currentBudget = budgetConfigurationViewStore.currentModalBudget;
        const budgetName = currentBudget ? currentBudget.name : '';
        return (
            <div className="pxl-modal budget-config-modal">
                <div className="pxl-modal__content">
                    <div className="flex-heading">
                        <h1 className="pxl-header-sm">{budgetConfigurationViewStore.title}</h1>
                        <button
                            className="close no-btn-style"
                            onClick={this.handleClickCloseBudgetSettings}
                        >
                            <i className="icon-cancel pxl-text-c11-5" />
                        </button>
                    </div>
                    <div className="row">
                        <div className="col-sm-6">
                            <FormGroup text="Budget Name">
                                <Input
                                    id="budgetName"
                                    type="text"
                                    value={budgetName}
                                    onChange={this.handleChangeBudgetName}
                                />
                            </FormGroup>
                        </div>
                    </div>
                    {this.renderAssociatedFieldSelector()}
                    {this.renderAssociatedFieldAlerts()}
                    {this.renderFieldHasBudgetUsingOtherProrationRuleAlerts()}
                    {this.renderFieldAlreadyHasDecisionsAlerts()}
                    {
                        this.renderCalculationRuleSettingsPanel(
                            AmountCode.Bonus,
                            'Bonus Percentage Calculation',
                            'bonusPercentageCalculationRuleOptions',
                            this.handleBonusPercentageRuleChange)
                    }
                    {
                        this.renderCalculationRuleSettingsPanel(
                            AmountCode.VariablePay,
                            'Variable Pay Percentage Calculation',
                            'variablePayPercentageCalculationRuleOptions',
                            this.handleVariablePayPercentageRuleChange)
                    }
                    {/*tslint:disable-next-line */}
                    {/*{this.renderMultiBonusChoice()}*/}
                    {/* TODO: Re-enable this once multi-comp bonus is finished PSP-21488*/}
                    {this.renderLumpSumSettingPanel()}
                    {this.renderStockSettingPanel()}
                    {this.renderBudgetControlPanel()}
                    <div className="flex-row">
                        {this.renderDeleteBudgetButton()}
                        <div>
                            <button className="pxl-link" onClick={this.handleClickCloseBudgetSettings}>Cancel</button>
                            <button
                                onClick={this.handleSaveBudget}
                                className={budgetConfigurationViewStore.buttonClassName}
                                disabled={budgetConfigurationViewStore.saveButtonDisabled}
                            >
                                {budgetConfigurationViewStore.saveButtonTitle}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private handleClickCloseBudgetSettings(): void {
        this.props.budgetConfigurationViewStore.closeBudgetSettingsModal();
    }

    private handleChangeBudgetName(_: undefined, value: string): void {
        if (this.props.budgetConfigurationViewStore.currentModalBudget) {
            this.props.budgetConfigurationViewStore.currentModalBudget.handleBudgetNameChange(value);
        }
    }

    private handleSaveBudget(): Promise<void> {
        return this.props.budgetConfigurationViewStore.saveBudgetSettings();
    }

    private renderAssociatedFieldSelector(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        const currentBudget = budgetConfigurationViewStore.currentModalBudget;
        const associatedFields = currentBudget ? currentBudget.associatedFields.slice() : [];
        return (
            <FormGroup text="Associated Fields">
                <Options
                    id="associatedFields"
                    options={budgetConfigurationViewStore.associatedFieldOptions}
                    onChange={this.handleAssociatedFieldsChanged}
                    syncOptions={false}
                    value={associatedFields}
                    type="pills"
                    horizontal={true}
                    optionProps={{'className': budgetConfigurationViewStore.associatedFieldsCss}}
                />
                <label htmlFor="associatedStockField" className="pxl-text--xs">--- or ---</label>
                <Options
                    id="associatedStockField"
                    options={[budgetConfigurationViewStore.stockField]}
                    onChange={this.handleStockAssociatedFieldClick}
                    syncOptions={false}
                    value={associatedFields}
                    type="pills"
                    horizontal={true}
                    optionProps={{'className': budgetConfigurationViewStore.stockFieldCss}}
                />
            </FormGroup>
        );
    }

    private handleAssociatedFieldsChanged(_: undefined, value: string[]): void {
        const { budgetConfigurationViewStore } = this.props;
        budgetConfigurationViewStore.handleAssociatedFieldsChange(value);
    }

    private handleStockAssociatedFieldClick(): void {
        const { budgetConfigurationViewStore } = this.props;
        budgetConfigurationViewStore.handleStockAssociatedFieldChange();
    }

    private renderAssociatedFieldAlerts(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        if (!budgetConfigurationViewStore.isFieldHasBeenUsed) {
            return null;
        }

        return Array.from(budgetConfigurationViewStore.overlappedFieldBudgets.keys()).map(entry => {
            return (
                <Alert
                    className="pxl-margin-bottom-sm"
                    key={entry}
                    message={this.renderAssociatedFieldAlertMessage(entry)}
                    level="info"
                    close={false}
                />
            );
        });
    }

    private renderFieldHasBudgetUsingOtherProrationRuleAlerts(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        return budgetConfigurationViewStore.fieldsWithOtherProrationRule.map(field => {
            // tslint:disable-next-line:max-line-length
            const message = `${field} has budget using another proration rule. ${field} cannot be chosen in this proration rule.`;
            return (
                <Alert
                    className="pxl-margin-bottom-sm"
                    key={field}
                    message={message}
                    level="danger"
                    close={false}
                />
            );
        });
    }

    private renderFieldAlreadyHasDecisionsAlerts(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        return budgetConfigurationViewStore.selectedFieldsHaveDecisions.map(field => {
            // tslint:disable-next-line:max-line-length
            const message = `<strong>${field}</strong> cannot be removed from this budget. Manager decisions have been made for this field.`;
            return (
                <Alert
                    className="pxl-margin-bottom-sm"
                    key={field}
                    message={message}
                    level="danger"
                    close={false}
                />
            );
        });
    }

    private renderAssociatedFieldAlertMessage(field: string): React.ReactNode {
        return (
            <div className="pxl-margin-top-xs">
                <strong>{field}</strong> is currently associated with another budget and will be moved to this one.
                If all fields are moved from a budget it will be deleted.
            </div>
        );
    }

    private renderCalculationRuleSettingsPanel(
        amountCode: AmountCode,
        text: string,
        id: string,
        onChange: (_: undefined, value: string) => void): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        if (!budgetConfigurationViewStore.hasAssociatedAmount(amountCode)) {
            return null;
        }

        const cycleConfiguration = budgetConfigurationViewStore.editableCycleConfiguration;
        if (!cycleConfiguration) {
            return null;
        }
        return (
            <FormGroup text={text}>
                <div className="pxl-text--sm pxl-margin-top-sm">
                    <label htmlFor={id} className="radio-block-label">Based on </label>
                    <div className="radio-block variable-pay">
                        <Options
                            id={id}
                            type="radio"
                            multiValue={false}
                            options={PercentageCalculationRuleOptions.map(option => option.text)}
                            value={[cycleConfiguration.getPercentageRuleOptionValue(amountCode)] ?? []}
                            onChange={onChange}
                            syncOptions={false}
                            horizontal={false}
                            optionProps={{'className': 'pxl-option--primary'}}
                        />
                    </div>
                </div>
            </FormGroup>
        );
    }

    private handleBonusMultiChoiceChange(_: undefined, value: string): void {
        this.setState({currentSelection: budgetSelection[value]});
    }

    private handleBonusPercentageRuleChange(_: undefined, value: string): void {
        const { editableCycleConfiguration } = this.props.budgetConfigurationViewStore;
        if (!editableCycleConfiguration) {
            return;
        }
        editableCycleConfiguration
            .handleAmountPercentageRuleChange(AmountCode.Bonus, value);
    }

    private handleVariablePayPercentageRuleChange(_: undefined, value: string): void {
        const { editableCycleConfiguration } = this.props.budgetConfigurationViewStore;
        if (!editableCycleConfiguration) {
            return;
        }
        editableCycleConfiguration
            .handleAmountPercentageRuleChange(AmountCode.VariablePay, value);
    }

    private renderLumpSumSettingPanel(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        if (!budgetConfigurationViewStore.editableCycleConfiguration ||
            !budgetConfigurationViewStore.hasAssociatedAmount(AmountCode.LumpSum)) {
            return null;
        }

        const lumpSum = budgetConfigurationViewStore.editableCycleConfiguration.cycleBudgetFields
            .find(budgetField => budgetField.amountCode === AmountCode.LumpSum);
        if (!lumpSum || !lumpSum.rangeEnforcement) {
            return null;
        }

        return (
            <FormGroup text="Lump Sum Calculation">
                <div className="pxl-margin-top-sm">
                    <Option
                        id="rangeEnforcementCheckbox"
                        checked={lumpSum.rangeEnforcement.useAutoCalculateLumpSum}
                        onChange={this.handleRangeEnforcementCheckboxChanged}
                        text="Automatically calculate the Lump Sum if the"
                        className="pxl-option--secondary"
                    />
                    <div className="indent">
                        <label htmlFor="rangeEnforcementPercentage" className="pxl-text-sm">
                            new salary is above
                            <Input
                                type="number"
                                id="rangeEnforcementPercentage"
                                className="inline-input"
                                value={String(lumpSum.rangeEnforcement.autoCalculatePercentage)}
                                onChange={this.handleAutoCalculatePercentageChange}
                            />
                                    % of the range maximum
                        </label>
                        <p className="example">Example: If the range maximum is $100,000 and the entry is 100%,<br />
                            Lump Sum will be auto-calculated if the new salary is above $100,000</p>
                        <Option
                            id="rangeEnforcementOveriddeCheckbox"
                            checked={lumpSum.rangeEnforcement.allowOverrideLumpSum}
                            onChange={this.handleAllowOverrideLumpSumChange}
                            text="Managers can override the calculated value"
                            className={this.rangeEnforcementOverrideInputCss(lumpSum.rangeEnforcement)}
                        />
                    </div>
                </div>
            </FormGroup>
        );
    }

    private rangeEnforcementOverrideInputCss(rangeEnforcement: IRangeEnforcement): string {
        if (rangeEnforcement?.useAutoCalculateLumpSum) {
            return 'pxl-option--secondary pxl-text-sm';
        }
        return 'pxl-option--secondary pxl-text-sm disabled';
    }

    // Below is a hackday project that has never come to fruition. Though there are 
    // those who would like to revive it. Until it becomes prioritzied it layeth to rest here.
    // Here lies the multi-component bonus in all of it's glory.
    /*
    private renderMultiBonusChoice(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;

        if (!budgetConfigurationViewStore.hasAssociatedAmount(AmountCode.Bonus)) {
            return null;
        }
        const currentValue = multiBudgetChoices[this.state.currentSelection];
        return (
            <div className="multi-bonus-choice">
                <FormGroup text="Bonus Components">

                    <div className="radio-block">
                        <Options
                            id="multi-bonus-choice-radio"
                            type="radio"
                            multiValue={false}
                            options={multiBudgetChoices}
                            value={currentValue}
                            onChange={this.handleBonusMultiChoiceChange}
                            syncOptions={false}
                            horizontal={true}
                            optionProps={{'className': 'pxl-option--primary'}}
                        />
                    </div>
                </FormGroup>
                {this.renderMultiBonusConfig(currentValue, 2)}
            </div>
        );
    }
    */

    /*
    private renderMultiBonusConfig(currentValue: string, _numRules: number): React.ReactNode {
        if (currentValue === 'Single Component Bonus') {
            return null;
        }
        return (
            <div className="multi-bonus-choice__config">
                <div className="multi-bonus-choice__number-rules">
                    <span>Breakdown bonus into</span>
                    <Dropdown
                        btnText="2"
                        btnClass="multi-bonus-choice__dropdown-btn"
                        id="multi-bonus-config-dropdown"
                    >
                        <button
                            className="pxl-dropdown__item"
                            onClick={() => this.setNumberBonusRules(2)}
                        >
                            2
                        </button>
                        <button
                            className="pxl-dropdown__item"
                            onClick={() => this.setNumberBonusRules(3)}
                        >
                            3
                        </button>
                        <button
                            className="pxl-dropdown__item"
                            onClick={() => this.setNumberBonusRules(4)}
                        >
                            4
                        </button>
                    </Dropdown>
                    <span>components</span>
                </div>

                <div className="multi-bonus-choice__rules">
                    <div className="multi-bonus-choice__rules-column-name">
                        <p className="multi-bonus-choice__rules-heading">Bonus Component Name</p>
                    </div>
                    <div className="multi-bonus-choice__rules-column-weight">
                        <p className="multi-bonus-choice__rules-heading">Weight (%)</p>
                    </div>
                    <div className="multi-bonus-choice__rules-column-attainment">
                        <p className="multi-bonus-choice__rules-heading">(Optional) Attainment (%)</p>
                    </div>
                </div>
                {this.renderBonusRuleAnswerFields()}
            </div>
        );
    }


    private renderBonusRuleAnswerFields(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        budgetConfigurationViewStore.initBonusFieldRules();
        return (
            <div>
                {budgetConfigurationViewStore.bonusFieldRules.map((rule, index) => {
                    return (
                        <BudgetModalBonusRule
                            key={index}
                            bonusFieldRule={rule}
                            fieldNameChange={budgetConfigurationViewStore.setBonsuRuleName}
                            splitFieldChange={budgetConfigurationViewStore.setBonsuRuleSplit}
                            attainedFieldChange={budgetConfigurationViewStore.setBonusRuleAttained}
                        />
                    );
                })}
            </div>
        );
    }
    */
    
    // eslint-disable-next-line no-unused-vars
    private setNumberBonusRules(_event: number): void {
        // TODO-hackweek tie to state(?) or store(?)
        // and trigger re-render of renderMultiBonusConfig so that the dropdown will close
        // because PXLv3 doesn't close the dropdown on click :( :( :(
        // console.log(event);
    }

    private handleAutoCalculatePercentageChange(_: undefined, value: string): void {
        const lumpSum = this.getLumpSum();
        if (lumpSum && lumpSum.rangeEnforcement) {
            lumpSum.rangeEnforcement.setAutoCalculatePercentage(Number(value));
        }
    }

    private handleAllowOverrideLumpSumChange(_: undefined, value: IOnChangeEvent): void {
        const lumpSum = this.getLumpSum();
        if (lumpSum && lumpSum.rangeEnforcement) {
            lumpSum.rangeEnforcement.setAllowOverrideLumpSum(value.target.checked);
        }
    }

    private handleRangeEnforcementCheckboxChanged(_: undefined, value: IOnChangeEvent): void {
        const lumpSum = this.getLumpSum();
        if (lumpSum && lumpSum.rangeEnforcement) {
            lumpSum.rangeEnforcement.setUseAutoCalculateLumpSum(value.target.checked);
        }
    }

    private getLumpSum(): Nullable<CycleBudgetField> {
        if (!this.props.budgetConfigurationViewStore.editableCycleConfiguration) {
            return null;
        }

        return this.props.budgetConfigurationViewStore.editableCycleConfiguration?.cycleBudgetFields?.find(
            budgetField => budgetField.amountCode === AmountCode.LumpSum) ?? null;
    }

    private renderStockSettingPanel(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        if (!budgetConfigurationViewStore.hasAssociatedAmount(AmountCode.Stock)) {
            return null;
        }

        const cycleConfiguration = budgetConfigurationViewStore.editableCycleConfiguration;
        const stockValueInput = budgetConfigurationViewStore.stockValueInput;
        if (!cycleConfiguration) {
            return null;
        }
        return (
            <FormGroup text="Show Stock As">
                <div className="radio-block pxl-text-sm pxl-margin-top-sm">
                    <Options
                        id="stockDisplayTypeOptions"
                        type="radio"
                        multiValue={false}
                        options={['Unit Value', 'Monetary Value']}
                        value={[cycleConfiguration.stockDisplayTypeOptionValue]}
                        onChange={this.handleStockDisplayChange}
                        syncOptions={false}
                        horizontal={false}
                        optionProps={{'className': 'pxl-option--primary'}}
                    />
                    <div className={this.stockValueCssClass(cycleConfiguration.getCycleBudgetField(AmountCode.Stock))}>
                        <label htmlFor="stockValueInput">Stock value
                            <Input
                                value={stockValueInput.getValueText()}
                                onChange={this.handleChangeStockValueInput}
                                onBlur={this.handleBlurStockValueInput}
                                disabled={!this.isStockBudgetMonetary(cycleConfiguration.getCycleBudgetField(AmountCode.Stock))}
                                className="inline-input"
                                id="stockValueInput"
                            />
                            per share</label>
                    </div>
                </div>
            </FormGroup>
        );
    }

    private renderBudgetControlPanel(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        const currentBudget = budgetConfigurationViewStore.currentModalBudget;
        if (!currentBudget) {
            return null;
        }

        return (
            <FormGroup text="Budget Control">
                <div className="pxl-margin-top-sm">
                    <Option
                        id="allowManagerToOverBudgetCheckbox"
                        checked={currentBudget.allowManagerOverBudget}
                        onChange={this.handleChangeManagerOverBudgetCheckbox}
                        text="Allow managers to go over this budget"
                        className="pxl-option--secondary"
                    />
                    <div className={budgetConfigurationViewStore.requireCommentCss}>
                        <div className="pxl-form-group">
                            <Option
                                id="requireCommentOnOverBudgetCheckbox"
                                checked={currentBudget.requireCommentWhenOverBudget}
                                onChange={this.handleChangeRequireCommentOnOverBudgetCheckbox}
                                text="Require comment when they are over budget"
                                className="pxl-option--secondary"
                                disabled={!currentBudget.allowManagerOverBudget}
                            />
                        </div>
                    </div>
                </div>
            </FormGroup>
        );
    }

    stockValueCssClass(stock: CycleBudgetField): string {
        if (stock && stock.stockDisplayType === StockDisplayType.Monetary) {
            return 'row indent';
        }
        return 'disabled row indent';
    }

    isStockBudgetMonetary(stock: CycleBudgetField): boolean {
        return stock.stockDisplayType === StockDisplayType.Monetary;
    }

    private renderDeleteBudgetButton(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        if (!budgetConfigurationViewStore.shouldRenderDeleteButton) {
            return null;
        }

        return (
            <button
                className="pxl-text-c10-light pxl-link"
                onClick={this.openDeleteBudgetConfirmation}
            >
                <i className="icon-trash"/> Delete This Budget
            </button>
        );
    }

    private handleChangeStockValueInput(_: undefined, value: string): void {
        this.props.budgetConfigurationViewStore.stockValueInput.changeValue(value);
    }

    private handleBlurStockValueInput(): void {
        this.props.budgetConfigurationViewStore.stockValueInput.onBlur();
    }

    private handleStockDisplayChange(_: undefined, value: string): void {
        const { editableCycleConfiguration } = this.props.budgetConfigurationViewStore;
        if (editableCycleConfiguration) {
            editableCycleConfiguration.handleStockDisplayChange(value);
        }
    }

    private handleChangeRequireCommentOnOverBudgetCheckbox(_: undefined, value: IOnChangeEvent): void {
        if (this.props.budgetConfigurationViewStore.currentModalBudget) {
            this.props.budgetConfigurationViewStore.currentModalBudget
                .handleRequireCommentOnOverBudget(value.target.checked);
        }
    }

    private handleChangeManagerOverBudgetCheckbox(_: undefined, value: IOnChangeEvent): void {
        if (this.props.budgetConfigurationViewStore.currentModalBudget) {
            this.props.budgetConfigurationViewStore.currentModalBudget
                .handleAllowManagerToOverBudget(value.target.checked);
        }
    }

    private openDeleteBudgetConfirmation(): void {
        this.props.budgetConfigurationViewStore.openDeleteBudgetConfirmationModal();
    }
}